import React, { Fragment } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import { Add, Delete, Edit, Check, SwapHoriz } from '@material-ui/icons';
import { ITypeTechnicalRoom } from 'pages/PageTypeTechnicalRoomPprList/interfaces';
import Excel from 'assets/icons/svg/ExcelIcon';
import Pdf from 'assets/icons/svg/PdfIcon';
import moment from 'moment';
import { EJournalEquipmentStatuses, EJournalEquipmentItemStatuses, IJournalEquipmentItem } from '../interfaces';

const useStyles = makeStyles({
  table: {
    '& thead': {
      '& th': {
        fontWeight: 600,
        backgroundColor: '#FDD7E7',
      },
      '& th:first-child': {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: '#FDD7E7',
      },
    },

    '& tbody': {
      '& tr': {
        '& td': {
          minWidth: 250,
          backgroundColor: '#FAFAFA',
          transition: 'background-color 200ms',
        },
        '&:hover': {
          '& td': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
  },
  stickyCell: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    backgroundColor: '#FAFAFA !important',
    '&:hover': {
      backgroundColor: '#f5f5f5 !important',
    },
  },
  actionsCell: {
    minWidth: '150px !important',
    width: '150px',
    maxWidth: '150px',
    position: 'sticky',
    left: '250px', // ширина первой колонки
    zIndex: 2,
    backgroundColor: '#FAFAFA !important',
    '&:hover': {
      backgroundColor: '#f5f5f5 !important',
    },
  },
  actionsCellHeader: {
    minWidth: '150px !important',
    width: '150px',
    maxWidth: '150px',
    position: 'sticky',
    left: '250px',
    zIndex: 3,
    backgroundColor: '#FDD7E7 !important',
  },
  equipmentNameCell: {
    minWidth: '250px !important',
    width: '250px',
    maxWidth: '250px',
    position: 'sticky',
    left: '400px', // 250px (первая) + 150px (вторая)
    zIndex: 2,
    backgroundColor: '#FAFAFA !important',
    '&:hover': {
      backgroundColor: '#f5f5f5 !important',
    },
  },
  equipmentNameCellHeader: {
    minWidth: '250px !important',
    width: '250px',
    maxWidth: '250px',
    position: 'sticky',
    left: '400px',
    zIndex: 3,
    backgroundColor: '#FDD7E7 !important',
  },
  narrowCell: {
    minWidth: '100px !important',
    width: '100px',
    maxWidth: '100px',
  },
  modelCell: {
    minWidth: '120px !important',
    width: '120px',
    maxWidth: '120px',
  },
});

interface IProps {
  typeTechnicalRoom: ITypeTechnicalRoom | null;
  equipmentTypes: Record<string, IJournalEquipmentItem[]>;
  journalEquipmentStatusId: EJournalEquipmentStatuses;
  disabled: boolean;
  canManageItemStatus: boolean;
  onOpenJournalEquipmentItemDialog(
    typeTechnicalRoom: ITypeTechnicalRoom | null,
    journalEquipmentItem: IJournalEquipmentItem,
  ): void;
  onOpenDeleteJournalEquipmentItemDialog(
    typeTechnicalRoom: ITypeTechnicalRoom | null,
    journalEquipmentItem: IJournalEquipmentItem,
  ): void;
  onMarkAsChecked(journalEquipmentItem: IJournalEquipmentItem): void;
  onOpenEquipmentReplacementDialog(journalEquipmentItem: IJournalEquipmentItem): void;
  onOpenEquipmentAddDialog(journalEquipmentItem: IJournalEquipmentItem): void;
  onExportXls(): void;
  onExportPdf(): void;
}

const JournalEquipment: React.FC<IProps> = ({
  typeTechnicalRoom,
  equipmentTypes,
  journalEquipmentStatusId,
  disabled,
  canManageItemStatus,
  onOpenJournalEquipmentItemDialog,
  onOpenDeleteJournalEquipmentItemDialog,
  onMarkAsChecked,
  onOpenEquipmentReplacementDialog,
  onOpenEquipmentAddDialog,
  onExportXls,
  onExportPdf,
}) => {
  // Styles
  const classes = useStyles();

  // Проверяем, нужно ли показывать колонку с действиями
  const showActionsColumn =
    journalEquipmentStatusId !== EJournalEquipmentStatuses.AGREEMENT ||
    Object.values(equipmentTypes)
      .flat()
      .some(
        (item) =>
          (item.statusId === EJournalEquipmentItemStatuses.ON_CHECK &&
            item.journalEquipmentItemId &&
            canManageItemStatus) ||
          (item.statusId === EJournalEquipmentItemStatuses.WRITTEN_OFF &&
            item.journalEquipmentItemId &&
            canManageItemStatus) ||
          (journalEquipmentStatusId !== EJournalEquipmentStatuses.AGREEMENT &&
            (item.journalEquipmentItemId || typeTechnicalRoom)),
      );

  // Main render
  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Box sx={{ maxHeight: '100vh', overflow: 'auto' }}>
          <Table stickyHeader size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Тип оборудования</TableCell>
                {showActionsColumn && <TableCell className={classes.actionsCellHeader} />}
                <TableCell
                  className={classes.equipmentNameCellHeader}
                  style={{ left: showActionsColumn ? '400px' : '250px' }}
                >
                  Наименование оборудования
                </TableCell>
                <TableCell>Производитель</TableCell>
                <TableCell className={classes.modelCell}>Модель</TableCell>
                <TableCell>Серийный номер</TableCell>
                <TableCell className={classes.narrowCell}>Количество</TableCell>
                <TableCell>Дата начала эксплуатации</TableCell>
                <TableCell>Дата обновления</TableCell>
                <TableCell className={classes.narrowCell}>Статус</TableCell>
                <TableCell>Срок эксплуатации (месяцы)</TableCell>
                <TableCell>Гарантийный срок (месяцы)</TableCell>
                <TableCell className={classes.narrowCell}>Стоимость</TableCell>
                <TableCell>Комментарий</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {Object.entries(equipmentTypes).map(([equipmentTypeName, journalEquipmentItems]) => {
                return (
                  <Fragment key={equipmentTypeName}>
                    <TableRow>
                      <TableCell rowSpan={journalEquipmentItems.length + 1} className={classes.stickyCell}>
                        <b>{equipmentTypeName}</b>
                      </TableCell>
                    </TableRow>

                    {journalEquipmentItems.map((journalEquipmentItem, index) => {
                      const {
                        journalEquipmentItemId,
                        equipmentName,
                        equipmentCount,
                        equipmentCountry,
                        equipmentModel,
                        equipmentSerialNumber,
                        equipmentCost,
                        dateStart,
                        monthCount,
                        comment,
                        guaranteeMonth,
                        statusId,
                        statusName,
                        updateAt,
                      } = journalEquipmentItem;

                      return (
                        <TableRow
                          key={journalEquipmentItemId ? journalEquipmentItemId : `${equipmentTypeName}-${index}`}
                        >
                          {showActionsColumn && (
                            <TableCell className={classes.actionsCell}>
                              {statusId === EJournalEquipmentItemStatuses.ON_CHECK &&
                              journalEquipmentItemId &&
                              canManageItemStatus ? (
                                <>
                                  <Tooltip title="Проверено">
                                    <IconButton
                                      size="small"
                                      onClick={() => onMarkAsChecked(journalEquipmentItem)}
                                      disabled={disabled}
                                    >
                                      <Check fontSize="small" color="primary" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Замена">
                                    <IconButton
                                      size="small"
                                      onClick={() => onOpenEquipmentReplacementDialog(journalEquipmentItem)}
                                      disabled={disabled}
                                    >
                                      <SwapHoriz fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : statusId === EJournalEquipmentItemStatuses.WRITTEN_OFF &&
                                journalEquipmentItemId &&
                                canManageItemStatus ? (
                                <Tooltip title="Добавить">
                                  <IconButton
                                    size="small"
                                    onClick={() => onOpenEquipmentAddDialog(journalEquipmentItem)}
                                    disabled={disabled}
                                  >
                                    <Add fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : journalEquipmentStatusId !== EJournalEquipmentStatuses.AGREEMENT ? (
                                journalEquipmentItemId ? (
                                  <>
                                    <Tooltip title="Редактировать">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          onOpenJournalEquipmentItemDialog(typeTechnicalRoom, journalEquipmentItem)
                                        }
                                        disabled={disabled}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Удалить">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          onOpenDeleteJournalEquipmentItemDialog(
                                            typeTechnicalRoom,
                                            journalEquipmentItem,
                                          )
                                        }
                                        disabled={disabled}
                                      >
                                        <Delete fontSize="small" color="error" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  typeTechnicalRoom && (
                                    <Tooltip title="Добавить">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          onOpenJournalEquipmentItemDialog(typeTechnicalRoom, journalEquipmentItem)
                                        }
                                        disabled={disabled}
                                      >
                                        <Add fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )
                                )
                              ) : null}
                            </TableCell>
                          )}

                          <TableCell
                            className={classes.equipmentNameCell}
                            style={{ left: showActionsColumn ? '400px' : '250px' }}
                          >
                            {equipmentName ?? '—'}
                          </TableCell>
                          <TableCell>{equipmentCountry ?? '—'}</TableCell>
                          <TableCell className={classes.modelCell}>{equipmentModel ?? '—'}</TableCell>
                          <TableCell>{equipmentSerialNumber.length ? equipmentSerialNumber.join(', ') : '—'}</TableCell>
                          <TableCell className={classes.narrowCell}>{equipmentCount ?? '—'}</TableCell>
                          <TableCell>{dateStart ?? '—'}</TableCell>
                          <TableCell>{updateAt ? moment(updateAt).format('DD.MM.YYYY') : '—'}</TableCell>
                          <TableCell className={classes.narrowCell}>{statusName ?? '—'}</TableCell>
                          <TableCell>{monthCount ?? '—'}</TableCell>
                          <TableCell>{guaranteeMonth ?? '—'}</TableCell>
                          <TableCell className={classes.narrowCell}>{equipmentCost}</TableCell>
                          <TableCell>{comment ?? '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
        <Tooltip title="Экспорт в Excel">
          <IconButton onClick={onExportXls} disabled={disabled}>
            <Excel style={{ color: '#3C763D' }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Экспорт в PDF">
          <IconButton onClick={onExportPdf} disabled={disabled}>
            <Pdf />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};

export default JournalEquipment;
